import React from 'react';
import { Modal, Loading } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useDeleteGroup from 'Hooks/query-hooks/global-rules/useDeleteGroups';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { useQueryClient } from '@tanstack/react-query';
import { RQ_GLOBAL_RULES, RQ_RULE_GROUPS } from 'Utils';

import styles from './DeleteGroupModal.css';

type Props = {
    onClose: () => void;
    deleteGroupId: number;
    isGroupDelete: boolean;
};

const DeleteGroupModal = ({ onClose, deleteGroupId, isGroupDelete }: Props) => {
    const { addAlert } = useAlertBanner();
    const queryClient = useQueryClient();

    const { t } = useTranslation();
    const { templateRepositoryId } = useTemplateStore.getState().template;

    const mutation = useDeleteGroup({
        mutationOptions: {
            onSuccess: () => {
                addAlert({
                    id: 'delete-group-success',
                    content: t('ruleGroupDeletedSuccessfully'),
                    status: 'success'
                });
                queryClient.invalidateQueries([RQ_GLOBAL_RULES, templateRepositoryId]);
                queryClient.invalidateQueries([RQ_RULE_GROUPS]);
                onClose();
            },
            onError: (err: { message: string; statusCode: number }[]) => {
                const statusText = err[0]?.message || t('Try Later');
                addAlert({
                    id: 'create-group-success',
                    content: statusText,
                    status: 'danger'
                });
                onClose();
            }
        },
        deleteGroupId
    });

    const { isLoading } = mutation;
    return (
        <Modal
            dataTestId='delete-group-modal'
            isOpen={isGroupDelete}
            closeModal={onClose}
            status='warning'
            containerClass={styles.modalContainer}
        >
            <Modal.Title
                id='delete-group-title'
                title={t('deleteGroup.deleteGroup')}
                statusIcon='warning'
                onClose={onClose}
            />
            <div className={styles.deleteWarningContent}>
                <div className={styles.sectionOne}>{t('deleteGroup.areYouSure')}</div>
            </div>
            <Modal.Footer
                id='delete-group-footer'
                onAction={() => mutation.mutate()}
                disableOnCancel={isLoading}
                disableOnAction={isLoading}
                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Delete Group')}
                onClose={onClose}
                onCloseText={t('Cancel')}
            />
        </Modal>
    );
};
export default DeleteGroupModal;
